import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useEnvironment } from '../context/EnvironmentContext';
import { useLayoutVariant } from '../context/LayoutVariantContext';
import { useNotifications } from '../context/NotificationsContext';
import { AlertTriangle, CheckCircle, User, CircleHelp, LogOut, Bell, Palette, Check, X, DollarSign, Shield, Info } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Button } from './ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from './ui/dialog';
import { cn } from '../lib/utils';

const roleLabel = (role) => {
  if (role === 'admin') return 'Super Admin';
  if (role === 'merchant_admin') return 'Merchant Admin';
  return role ? role.charAt(0).toUpperCase() + role.slice(1).replace('_', ' ') : 'User';
};

const INTERNAL_ROLES = new Set(['admin', 'compliance_officer', 'accounts', 'operations']);

const TopBar = () => {
  const { user, logout } = useAuth();
  const { isLive, toggleEnvironment } = useEnvironment();
  const { isReference, toggleVariant } = useLayoutVariant();
  const { notifications, unreadCount, markAsRead, markAllRead, removeNotification } = useNotifications();
  const navigate = useNavigate();
  const location = useLocation();
  const [bellOpen, setBellOpen] = useState(false);
  const [envSwitchDialog, setEnvSwitchDialog] = useState(null);
  const dismiss = removeNotification;
  const isInternal = user && (INTERNAL_ROLES.has(user.role) || (user.permissions || []).some(p => INTERNAL_ROLES.has(p)));
  const notificationsPath = isInternal ? '/backoffice/notifications/log' : '/notifications';

  const handleEnvSwitchConfirm = () => {
    if (envSwitchDialog) {
      toggleEnvironment();
      setEnvSwitchDialog(null);
      window.location.reload();
    }
  };
  const isBackOffice = location.pathname.startsWith('/backoffice');
  const isSuperAdmin = user?.role === 'admin';
  const showMerchantNav = isReference && !isSuperAdmin && !isBackOffice;

  const notifIcon = (type) => {
    switch (type) {
      case 'kyc':
      case 'payment': return <CheckCircle className="w-5 h-5 text-[#1C472B]" />;
      case 'transfer': return <Info className="w-5 h-5 text-blue-500" />;
      case 'fx': return <DollarSign className="w-5 h-5 text-amber-500" />;
      default: return <Info className="w-5 h-5 text-slate-500" />;
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  if (isReference) {
    const refPath = window.location.pathname || '/dashboard';
    const refNav = [
      { to: '/dashboard', label: 'Dashboard' },
      { to: '/payments', label: 'Transfers' },
      { to: '/analytics', label: 'Analytics' },
      { to: '/settings', label: 'Settings' },
    ];
    const initials = (user?.name || 'U').split(' ').map((n) => n[0]).join('').slice(0, 2).toUpperCase();

    return (
      <>
      <div className="fixed top-0 left-0 right-0 z-50 h-16 bg-white border-b border-[#DCE4E0] flex items-center justify-between px-4">
        <div className="flex items-center gap-3">
          <img src="/logo.png" alt="PayZen" className="w-10 h-10 rounded-xl object-contain" />
          <h1 className="font-bold text-lg text-[#073B2E] tracking-tight">PayZen</h1>
        </div>

        <nav className="hidden md:flex items-center gap-6">
          {showMerchantNav ? (
            refNav.map(({ to, label }) => (
              <Link
                key={to}
                to={to}
                className={cn(
                  'text-sm transition-colors',
                  refPath === to || (to !== '/dashboard' && (refPath === to || refPath.startsWith(to + '/')))
                    ? 'font-semibold text-[#0B6445]'
                    : 'font-medium text-[#0E1B16] hover:text-[#0B6445]'
                )}
              >
                {label}
              </Link>
            ))
          ) : (isSuperAdmin || isBackOffice) ? (
            <span className="text-sm font-medium text-[#0E1B16]/80">Back Office</span>
          ) : null}
        </nav>

        <div className="flex items-center gap-4">
          <div className="flex rounded-lg overflow-hidden border border-slate-200 bg-slate-50 p-0.5">
            <button
              type="button"
              onClick={() => isLive && setEnvSwitchDialog('sandbox')}
              className={cn(
                'flex items-center gap-1.5 px-3 py-1.5 text-sm font-semibold rounded-md transition-colors',
                !isLive ? 'bg-amber-400 text-amber-950 shadow-sm' : 'text-slate-600 hover:text-slate-900'
              )}
              data-testid="env-sandbox"
              title="Sandbox – test data"
            >
              <AlertTriangle className="w-4 h-4" />
              SANDBOX
            </button>
            <button
              type="button"
              onClick={() => !isLive && setEnvSwitchDialog('live')}
              className={cn(
                'flex items-center gap-1.5 px-3 py-1.5 text-sm font-semibold rounded-md transition-colors',
                isLive ? 'bg-[#E6F7F3] text-[#0B6445] shadow-sm border border-emerald-300' : 'text-slate-600 hover:text-slate-900'
              )}
              data-testid="env-live"
              title="Live – production data (dashboard refreshes)"
            >
              <CheckCircle className="w-4 h-4" />
              LIVE
            </button>
          </div>

          <Popover open={bellOpen} onOpenChange={setBellOpen}>
            <PopoverTrigger asChild>
              <button
                type="button"
                className="relative p-2 rounded-full hover:bg-slate-100 text-[#0E1B16]"
                aria-label="Notifications"
              >
                <Bell className="w-5 h-5" />
                {unreadCount > 0 && (
                  <span className="absolute top-1 right-1 flex h-4 w-4 items-center justify-center rounded-full bg-red-500 text-[10px] font-bold text-white">
                    {unreadCount}
                  </span>
                )}
              </button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-[380px] p-0 rounded-xl border-[#DCE4E0] shadow-lg">
              <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
                <div>
                  <h3 className="font-bold text-[#073B2E]">Notifications</h3>
                  <p className="text-xs text-slate-500">{unreadCount} unread notifications</p>
                </div>
                {unreadCount > 0 && (
                  <button type="button" onClick={markAllRead} className="text-sm font-medium text-slate-600 hover:text-[#073B2E] border border-slate-200 rounded-lg px-3 py-1.5 hover:bg-slate-50">
                    Mark all as read
                  </button>
                )}
              </div>
              <div className="max-h-[360px] overflow-y-auto">
                {notifications.length === 0 ? (
                  <p className="text-sm text-slate-500 py-6 text-center">No notifications</p>
                ) : (
                  notifications.map((n) => {
                    const actionPath = n.details?.action_path || (n.details?.merchant_id && isInternal ? `/backoffice/kyc/pending?merchant_id=${n.details.merchant_id}` : null);
                    const to = actionPath || `${notificationsPath}?highlight=${n.id}`;
                    return (
                    <div key={n.id} className="flex gap-3 px-4 py-3 border-b border-slate-100 last:border-0 hover:bg-slate-50/50 transition-colors border-l-4 border-l-[#1C472B]">
                      <div className="w-9 h-9 rounded-full bg-[#E6F7F3] flex items-center justify-center shrink-0">{notifIcon(n.type)}</div>
                      <Link
                        to={to}
                        onClick={() => setBellOpen(false)}
                        className="flex-1 min-w-0 cursor-pointer no-underline"
                      >
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-semibold text-[#0F172A] text-sm">{n.title}</span>
                          {(n.isNew ?? !n.read) && (
                            <span className="rounded bg-[#1C472B] text-white text-[10px] font-semibold px-1.5 py-0.5">New</span>
                          )}
                        </div>
                        <p className="text-xs text-slate-600 mt-0.5 line-clamp-2">{n.message}</p>
                        <p className="text-[11px] text-slate-400 mt-1">{n.time}</p>
                      </Link>
                      <div className="flex flex-col gap-1 shrink-0" onClick={(e) => e.stopPropagation()}>
                        {(n.isNew ?? !n.read) && (
                          <button type="button" onClick={() => markAsRead(n.id)} className="p-1.5 rounded hover:bg-slate-200 text-slate-500" aria-label="Mark read"><Check className="w-4 h-4" /></button>
                        )}
                        <button type="button" onClick={() => dismiss(n.id)} className="p-1.5 rounded hover:bg-slate-200 text-slate-500" aria-label="Dismiss"><X className="w-4 h-4" /></button>
                      </div>
                    </div>
                  ); })
                )}
              </div>
              <div className="px-4 py-3 border-t border-slate-100">
                <Link to={notificationsPath} onClick={() => setBellOpen(false)} className="text-sm font-medium text-[#0B6445] hover:underline">
                  View all notifications
                </Link>
              </div>
            </PopoverContent>
          </Popover>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                className="flex items-center gap-2 rounded-full focus:outline-none focus:ring-2 focus:ring-[#0B6445] focus:ring-offset-2"
                data-testid="user-menu-trigger"
              >
                <span className="flex h-9 w-9 items-center justify-center rounded-full bg-[#0B6445] text-sm font-semibold text-white">
                  {initials}
                </span>
                <span className="hidden sm:inline text-sm font-medium text-[#0E1B16]">{user?.name || 'User'}</span>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <div className="px-3 py-3 border-b border-slate-100">
                <p className="text-sm font-semibold text-slate-900 truncate">{user?.name}</p>
                <p className="text-xs text-slate-500">{roleLabel(user?.role)}</p>
              </div>
              <DropdownMenuItem asChild>
                <Link to="/settings" className="cursor-pointer flex items-center gap-2 py-2">
                  <User className="w-4 h-4" />
                  Profile
                </Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link to="/info" className="cursor-pointer flex items-center gap-2 py-2">
                  <CircleHelp className="w-4 h-4" />
                  Help
                </Link>
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={handleLogout}
                className="cursor-pointer text-red-600 focus:text-red-600 flex items-center gap-2 py-2"
                data-testid="logout-btn"
              >
                <LogOut className="w-4 h-4" />
                Log out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          <button
            type="button"
            onClick={toggleVariant}
            className="flex items-center gap-2 px-3 py-2 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 hover:text-slate-900 text-sm font-medium"
            title="Switch to classic look"
          >
            <Palette className="w-4 h-4" />
            Old look
          </button>
        </div>
      </div>

      <Dialog open={!!envSwitchDialog} onOpenChange={(open) => { if (!open) setEnvSwitchDialog(null); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-[#0F172A]">
              {envSwitchDialog === 'live' ? "You'll be redirected to Live Mode." : "You'll be redirected to Test Mode."}
            </DialogTitle>
            <DialogDescription className="text-slate-600">
              {envSwitchDialog === 'live'
                ? "Once you're in Live Mode, all transactions will be made with real payments."
                : "Once you're in Test Mode, all transactions will be made with your test balance, with no actual money involved."}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setEnvSwitchDialog(null)} className="border-slate-300 text-slate-700 hover:bg-slate-100 hover:text-slate-900">
              Cancel
            </Button>
            <Button
              className={envSwitchDialog === 'live' ? 'bg-[#0B6445] hover:bg-[#095b3d] text-white' : 'bg-slate-700 hover:bg-slate-800 text-white'}
              onClick={handleEnvSwitchConfirm}
            >
              {envSwitchDialog === 'live' ? 'Switch to Live Mode' : 'Switch to Test Mode'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      </>
    );
  }

  return (
    <>
    <div className="fixed top-0 left-0 right-0 z-50 h-16 bg-[#1C472B] border-b border-[#143620] flex items-center justify-between px-6">
      <div className="flex items-center gap-3">
        <img src="/logo.png" alt="PayZen" className="w-10 h-10 rounded-xl object-contain" />
        <div>
          <h1 className="font-bold text-lg  text-white">PayZen</h1>
          <p className="text-xs text-slate-400">Merchant Hub</p>
        </div>
      </div>

      <div className="flex items-center gap-6">
        <div className="flex rounded-lg overflow-hidden border border-white/20 bg-white/5 p-0.5">
          <button
            type="button"
            onClick={() => isLive && setEnvSwitchDialog('sandbox')}
            className={cn(
              'flex items-center gap-1.5 px-3 py-1.5 text-sm font-semibold rounded-md transition-colors',
              !isLive ? 'bg-amber-500 text-amber-950 shadow-sm' : 'text-white/70 hover:text-white'
            )}
            data-testid="env-sandbox"
            title="Sandbox – test data"
          >
            <AlertTriangle className="w-4 h-4" />
            SANDBOX
          </button>
          <button
            type="button"
            onClick={() => !isLive && setEnvSwitchDialog('live')}
            className={cn(
              'flex items-center gap-1.5 px-3 py-1.5 text-sm font-semibold rounded-md transition-colors',
              isLive ? 'bg-emerald-400 text-white shadow-sm border border-emerald-500' : 'text-white/70 hover:text-white'
            )}
            data-testid="env-live"
            title="Live – production data (dashboard refreshes)"
          >
            <CheckCircle className="w-4 h-4" />
            LIVE
          </button>
        </div>

        <button
          type="button"
          onClick={toggleVariant}
          className="flex items-center gap-2 px-3 py-2 rounded-lg border border-white/30 text-white/90 hover:bg-white/10 hover:text-white text-sm font-medium"
          title="Switch to new look"
        >
          <Palette className="w-4 h-4" />
          New look
        </button>

        <Popover open={bellOpen} onOpenChange={setBellOpen}>
          <PopoverTrigger asChild>
            <button type="button" className="relative p-2 rounded-full hover:bg-white/10 text-white/90" aria-label="Notifications">
              <Bell className="w-5 h-5" />
              {unreadCount > 0 && (
                <span className="absolute top-1 right-1 flex h-4 w-4 items-center justify-center rounded-full bg-red-500 text-[10px] font-bold text-white">
                  {unreadCount}
                </span>
              )}
            </button>
          </PopoverTrigger>
          <PopoverContent align="end" className="w-[380px] p-0 rounded-xl border-[#DCE4E0] shadow-lg">
            <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
              <div>
                <h3 className="font-bold text-[#073B2E]">Notifications</h3>
                <p className="text-xs text-slate-500">{unreadCount} unread notifications</p>
              </div>
              {unreadCount > 0 && (
                <button type="button" onClick={markAllRead} className="text-sm font-medium text-slate-600 hover:text-[#073B2E] border border-slate-200 rounded-lg px-3 py-1.5 hover:bg-slate-50">
                  Mark all as read
                </button>
              )}
            </div>
            <div className="max-h-[360px] overflow-y-auto">
              {notifications.length === 0 ? (
                <p className="text-sm text-slate-500 py-6 text-center">No notifications</p>
              ) : (
                notifications.map((n) => {
                    const actionPath = n.details?.action_path || (n.details?.merchant_id && isInternal ? `/backoffice/kyc/pending?merchant_id=${n.details.merchant_id}` : null);
                    const to = actionPath || `${notificationsPath}?highlight=${n.id}`;
                    return (
                    <div key={n.id} className="flex gap-3 px-4 py-3 border-b border-slate-100 last:border-0 hover:bg-slate-50/50 transition-colors border-l-4 border-l-[#1C472B]">
                    <div className="w-9 h-9 rounded-full bg-[#E6F7F3] flex items-center justify-center shrink-0">{notifIcon(n.type)}</div>
                    <Link to={to} onClick={() => setBellOpen(false)} className="flex-1 min-w-0 cursor-pointer no-underline">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold text-[#0F172A] text-sm">{n.title}</span>
                        {(n.isNew ?? !n.read) && <span className="rounded bg-[#1C472B] text-white text-[10px] font-semibold px-1.5 py-0.5">New</span>}
                      </div>
                      <p className="text-xs text-slate-600 mt-0.5 line-clamp-2">{n.message}</p>
                      <p className="text-[11px] text-slate-400 mt-1">{n.time}</p>
                    </Link>
                    <div className="flex flex-col gap-1 shrink-0" onClick={(e) => e.stopPropagation()}>
                      {(n.isNew ?? !n.read) && (
                        <button type="button" onClick={() => markAsRead(n.id)} className="p-1.5 rounded hover:bg-slate-200 text-slate-500" aria-label="Mark read"><Check className="w-4 h-4" /></button>
                      )}
                      <button type="button" onClick={() => dismiss(n.id)} className="p-1.5 rounded hover:bg-slate-200 text-slate-500" aria-label="Dismiss"><X className="w-4 h-4" /></button>
                    </div>
                  </div>
                ); })
              )}
            </div>
            <div className="px-4 py-3 border-t border-slate-100">
              <Link to={notificationsPath} onClick={() => setBellOpen(false)} className="text-sm font-medium text-[#0B6445] hover:underline">
                View all notifications
              </Link>
            </div>
          </PopoverContent>
        </Popover>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="w-10 h-10 rounded-full bg-[#E6F7F3] hover:bg-[#A5E0D2] text-[#1C472B] p-0"
              data-testid="user-menu-trigger"
            >
              <User className="w-5 h-5" strokeWidth={2} />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <div className="px-3 py-3 border-b border-slate-100">
              <p className="text-sm font-semibold text-slate-900 truncate">{user?.name}</p>
              <p className="text-xs text-slate-500">{roleLabel(user?.role)}</p>
            </div>
            <DropdownMenuItem asChild>
              <Link to="/settings" className="cursor-pointer flex items-center gap-2 py-2">
                <User className="w-4 h-4" />
                Profile
              </Link>
            </DropdownMenuItem>
            <DropdownMenuItem asChild>
              <Link to="/info" className="cursor-pointer flex items-center gap-2 py-2">
                <CircleHelp className="w-4 h-4" />
                Help
              </Link>
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={handleLogout}
              className="cursor-pointer text-red-600 focus:text-red-600 flex items-center gap-2 py-2"
              data-testid="logout-btn"
            >
              <LogOut className="w-4 h-4" />
              Log out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>

    <Dialog open={!!envSwitchDialog} onOpenChange={(open) => { if (!open) setEnvSwitchDialog(null); }}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="text-[#0F172A]">
            {envSwitchDialog === 'live' ? "You'll be redirected to Live Mode." : "You'll be redirected to Test Mode."}
          </DialogTitle>
          <DialogDescription className="text-slate-600">
            {envSwitchDialog === 'live'
              ? "Once you're in Live Mode, all transactions will be made with real payments."
              : "Once you're in Test Mode, all transactions will be made with your test balance, with no actual money involved."}
          </DialogDescription>
        </DialogHeader>
<DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={() => setEnvSwitchDialog(null)} className="border-slate-300 text-slate-700 hover:bg-slate-100 hover:text-slate-900">
            Cancel
          </Button>
            <Button
              className={envSwitchDialog === 'live' ? 'bg-[#0B6445] hover:bg-[#095b3d] text-white' : 'bg-slate-700 hover:bg-slate-800 text-white'}
            onClick={handleEnvSwitchConfirm}
          >
            {envSwitchDialog === 'live' ? 'Switch to Live Mode' : 'Switch to Test Mode'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    </>
  );
};

export default TopBar;
